import React, { useEffect, useState, useCallback, useContext } from "react";
import axios from "axios";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Stack from "@mui/material/Stack";
import { AuthContext } from "../../contexts/user-context/user-context";
import { API_ENDPOINT } from "../../configurations/global.config";
import { Box, Button, Typography } from "@mui/material";
import { format } from "date-fns";

type BannerLevel = "critical" | "high" | "medium" | "low" | "info";

interface Banner {
  id: number;
  version: string;
  enabled: boolean;
  created_at: string;
  level: BannerLevel;
  title?: string | null;
  message: string;
  auto_dismiss_seconds?: number | null;
  show_learn_more: boolean;
  learn_more_url?: string | null;
}

const levelToMuiSeverity: Record<string, "error" | "warning" | "info" | "success"> = {
  critical: "error",
  high: "warning",
  medium: "info",
  low: "success",
  info: "info",
};

const levelToIcon = {
  critical: <ErrorIcon sx={{ color: "#fff" }} />,
  high: <WarningIcon sx={{ color: "#fff" }} />,
  medium: <InfoIcon sx={{ color: "#fff" }} />,
  low: <CheckCircleIcon sx={{ color: "#fff" }} />,
  info: <InfoIcon sx={{ color: "#fff" }} />,
};


const STORAGE_KEY = "dismissed_banners_versions_v2"; // Changed key to force reset

const AppBannerList: React.FC = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [dismissedVersions, setDismissedVersions] = useState<Set<string>>(new Set());
  const { config } = useContext(AuthContext);

  console.log("dismissed = ", dismissedVersions)

  // Load dismissed banner versions from localStorage
  useEffect(() => {
    console.log("Loading dismissed versions from localStorage...");
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      console.log("Stored value:", stored);
      if (stored) {
        const parsed = JSON.parse(stored);
        console.log("Parsed value:", parsed);
        if (Array.isArray(parsed)) {
          setDismissedVersions(new Set(parsed));
          console.log("Set dismissed versions:", parsed);
        }
      }
    } catch (error) {
      console.error("Error loading dismissed versions:", error);
    }
  }, []);

  // Save dismissed banner versions to localStorage
  const saveDismissedVersions = useCallback((versions: Set<string>) => {
    console.log("Saving dismissed versions:", versions);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(versions)));
      console.log("Successfully saved to localStorage");
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  }, []);

  // Fetch active banners from backend
  useEffect(() => {
    console.log("Fetching banners...");
    const fetchBanners = async () => {
      try {
        // const response = await axios.get<Banner[]>(`${API_ENDPOINT}/v1/banners/`, config);
        // console.log("Fetched banners:", response.data);
        // setBanners(response.data);
      } catch (error) {
        console.error("Error fetching banners:", error);
      }
    };
    fetchBanners();
  }, [config]);

  // Handle dismiss click
  const handleDismiss = useCallback((version: string) => {
    console.log("Dismissing version:", version);
    setDismissedVersions(prev => {
      const updated = new Set(prev);
      updated.add(version);
      console.log("New dismissed versions:", updated);
      saveDismissedVersions(updated);
      return updated;
    });
  }, [saveDismissedVersions]);

  // Filter banners to those not dismissed
  const visibleBanners = banners.filter((b) => {

    const isDismissed = dismissedVersions.has(b.version);
    console.log(`Banner ${b.version}: enabled=${b.enabled}, dismissed=${isDismissed}`);
    return !isDismissed && b.enabled;
  });

  // Auto dismiss effect for banners with autoDismissSeconds
  useEffect(() => {
    if (visibleBanners.length === 0) return;

    const timers: NodeJS.Timeout[] = [];

    visibleBanners.forEach((banner) => {
      if (banner.auto_dismiss_seconds && banner.auto_dismiss_seconds > 0) {
        const timer = setTimeout(() => {
          console.log("Auto-dismissing banner:", banner.version);
          handleDismiss(banner.version);
        }, banner.auto_dismiss_seconds * 1000);
        timers.push(timer);
      }
    });

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [visibleBanners, handleDismiss]);

  console.log("Current dismissed versions:", dismissedVersions);
  console.log("Visible banners:", visibleBanners);

  if (visibleBanners.length === 0) {
    console.log("No visible banners to display");
    return null;
  }

  if (!banners) return null
  return (
    <Box justifyContent={"center"} display={"flex"} mt={2}>
      <Stack spacing={2} sx={{ width: "90%", maxWidth: 1000 }}>
        {visibleBanners.map((banner) => (
          <Alert
            key={banner.version}
            severity={levelToMuiSeverity[banner.level] || "info"}
            icon={levelToIcon[banner.level] || <InfoIcon />}
            action={
                <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => handleDismiss(banner.version)}
                >
                <CloseIcon fontSize="inherit" />
                </IconButton>
            }
            variant="filled"
            sx={{
                borderRadius: 2,
                boxShadow: 3,
                px: 2,
                py: 1.5,
                color: "#fff",
                background: (theme) => {
                switch (banner.level) {
                    case "critical":
                    return "linear-gradient(45deg, #b71c1c, #f44336)";
                    case "high":
                    return "linear-gradient(45deg, #f57c00, #ffb300)";
                    case "medium":
                    return "linear-gradient(45deg, #0288d1, #03a9f4)";
                    case "low":
                    return "linear-gradient(45deg, #388e3c, #66bb6a)";
                    case "info":
                    default:
                    return "linear-gradient(45deg, #1976d2, #64b5f6)";
                }
                },
            }}
            >

            {banner.title && <AlertTitle>
                
                <b>{banner.title}</b>
                
                <Typography variant="subtitle2">{format(banner.created_at, "dd-MM-yyyy HH:mm")}</Typography>
                </AlertTitle>}
            <div style={{ whiteSpace: "pre-wrap" }}>
                {banner.message.split("\n").map((line, index) => (
                    <React.Fragment key={index}>
                    {line}
                    <br />
                    </React.Fragment>
                ))}
                {banner.show_learn_more && banner.learn_more_url && (
                    <>
                    <br />
                         <Link
                            href={banner.learn_more_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{ color: "#fff", fontWeight: 500, ml: 1 }}
                        >
                            Learn More
                        </Link>
                    </>
                )}
                </div>

          </Alert>
        ))}
      </Stack>
    </Box>
  );
};

export default AppBannerList;